import { useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useBusinessContext } from "../context/BusinessContext";

export type BusinessScopeOption = { id: string; name: string };

export type BookingScope = {
  /** SSO `admin` role — may operate across businesses (and see "all" oversight). */
  isAdmin: boolean;
  /**
   * THE single source of truth for "which business am I working in".
   * Both roles read this; it's set globally via the shell `BusinessSwitcher`
   * (or the /business overview page). Replaces the per-page `filterBusinessId`
   * state that used to diverge between the global selection and each page.
   * `undefined` = no business chosen yet.
   */
  effectiveBusinessId: string | undefined;
  /** Whether to render a switcher control (admin with >1 accessible business). */
  canPickBusiness: boolean;
  /** Normalized {id,name} list for switchers / dropdowns. */
  businessOptions: BusinessScopeOption[];
  /** Active business display name (for labels), or null when none/can't resolve. */
  activeBusinessName: string | null;
  selectBusinessId: (id: string | null) => void;
};

/**
 * Centralizes the business-scope decision that was previously scattered as
 * `admin` / `effectiveBusinessId` / `filterBusinessId` ternaries across the
 * bookings page and re-implemented as a `<Select>` in every toolbar. One
 * place, one source of truth (`BusinessContext.selectedBusinessId`), so the
 * admin global-vs-page-local divergence can't happen.
 */
export function useBookingScope(): BookingScope {
  const { hasBookingAdmin } = useAuth();
  const { businesses, selectedBusinessId, selectBusinessId } =
    useBusinessContext();

  const isAdmin = hasBookingAdmin();

  const businessOptions = useMemo<BusinessScopeOption[]>(
    () =>
      businesses.map((b) => {
        const id = String(b.id ?? b.businessId ?? "");
        return {
          id,
          name:
            (b.name as string) ||
            (b.businessName as string) ||
            (b.title as string) ||
            id,
        };
      }),
    [businesses],
  );

  const activeBusinessName = useMemo(() => {
    if (!selectedBusinessId) return null;
    return (
      businessOptions.find((b) => b.id === selectedBusinessId)?.name ?? null
    );
  }, [businessOptions, selectedBusinessId]);

  return {
    isAdmin,
    effectiveBusinessId: selectedBusinessId || undefined,
    canPickBusiness: isAdmin && businessOptions.length > 1,
    businessOptions,
    activeBusinessName,
    selectBusinessId,
  };
}
